import { useEffect, useMemo, useState } from "react";
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  Table,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Tooltip,
  Chip,
} from "@heroui/react";
import {
  Document,
  Edit,
  External,
  Eye,
  PlusCircle,
  Trash,
  Upload,
} from "assets/icons";
import ButtonNew from "components/buttonNew/buttonNew";
import {
  ContactType,
  ContractContact,
  ContractLegalForm,
  ContractPhase,
  MilestoneDateType,
  MilestoneOrigin,
  PropertyTaxes,
  RentalContract,
  RentalGuarantee,
} from "shared/types/rental-contracts";
import {
  EditableInputComponent,
  EditableSelectComponent,
  renderSection,
} from "components/index";
import { TableBody } from "@react-stately/table";
import { Line } from "react-chartjs-2";
import "chart.js/auto";
import { Chart, registerables } from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";
import ModalUploadFileLog from "shared/components/uploadFileModal/uploadFileModal";
import { formatDate, isDatePast } from "shared/utils/get-dates";
import { FileLabel, FileLog } from "shared/types/file";
import { Link } from "react-router-dom";
import {
  File,
  Info,
  LinkBreak,
  PlugsConnected,
  Recycle,
  UploadSimple,
} from "@phosphor-icons/react";
import ConnectContactModal from "./connectContactModal";
import AddMilestoneDateModal from "./addMilestoneDateModal";
import { useUsers } from "shared/contexts/users.context";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { useSession } from "@clerk/clerk-react";
import ConfirmationModal from "shared/components/confirmation/confirmation-modal";
import { toast } from "react-toastify";
import { API_URL } from "constants/index";
import { isArray } from "lodash";
import { Resource } from "shared/types/resource";
import { Calendar } from "@phosphor-icons/react";
import { SetFuturePriceModal } from "./modals/setFuturePrice";
Chart.register(...registerables, annotationPlugin);

interface ModalViewContractProps {
  isOpen: boolean;
  onClose: () => void;
  incomingContract: RentalContract;
  fetchRentalContracts: () => void;
}

const ModalViewContract = ({
  isOpen,
  onClose,
  incomingContract,
  fetchRentalContracts,
}: ModalViewContractProps) => {
  const [editedContract, setEditedContract] =
    useState<RentalContract>(incomingContract);
  const [contract, setContract] = useState<RentalContract>(incomingContract);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [isLoading] = useState<boolean>(false);
  const [chartData, setChartData] = useState({ labels: [], datasets: [] });
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isConnectContactModalOpen, setIsConnectContactModalOpen] =
    useState(false);
  const [isAddMilestoneDateModalOpen, setIsAddMilestoneDateModalOpen] =
    useState(false);
  const [isAddRentalGuaranteeModalOpen, setIsAddRentalGuaranteeModalOpen] =
    useState(false);
  const [
    isRecalculateMilestonesModalOpen,
    setIsRecalculateMilestonesModalOpen,
  ] = useState(false);
  const [isSetFuturePriceModalOpen, setIsSetFuturePriceModalOpen] =
    useState(false);
  const [selectedProductForFuturePrice, setSelectedProductForFuturePrice] =
    useState(null);
  const [unlinkContactId, setUnlinkContactId] = useState<number | null>(null);
  const [unlinkContactName, setUnlinkContactName] = useState<string>("");
  const { users } = useUsers();
  const { session } = useSession();

  // Attribute-only contracts (e.g. a standalone parking/garage) have no unit. Documents then
  // belong to the contract's single building attribute and are stored in that attribute's own
  // SharePoint folder. We pass the contract's building (which IS set on such contracts) so the
  // upload modal's "gebouw of unit" validation passes, plus the attribute id so the backend
  // routes the file to the attribute folder.
  const attributeOnlyProduct = useMemo(
    () =>
      !contract?.unit
        ? (contract?.products || []).find((p) => p.buildingAttribute?.id)
        : undefined,
    [contract],
  );
  const uploadBuilding =
    (contract && contract.unit && contract.unit.building) ||
    (contract && contract.building) ||
    null;
  const uploadUnit = (contract && contract.unit) || null;
  const attributeUploadCustomData = attributeOnlyProduct?.buildingAttribute?.id
    ? { buildingAttributeId: attributeOnlyProduct.buildingAttribute.id }
    : {};

  useEffect(() => {
    if (incomingContract && incomingContract.products) {
      setChartData(prepareChartData(incomingContract.products, contract));
    } else {
      setChartData({ labels: [], datasets: [] });
    }
  }, [incomingContract, incomingContract && incomingContract.products]);

  useEffect(() => {
    setContract(incomingContract);
  }, [incomingContract]);

  const LineChart = ({ data }: any) => {
    return <Line data={data} />;
  };

  const openUploadModal = () => {
    setIsUploadModalOpen(true);
  };

  const onOpenConnectContactModal = () => {
    setIsConnectContactModalOpen(true);
  };

  const onOpenAddMilestoneDateModal = () => {
    setIsAddMilestoneDateModalOpen(true);
  };

  const openRecalculateMilestonesModal = () => {
    setIsRecalculateMilestonesModalOpen(true);
  };

  const onOpenAddRentalGuarantee = () => {
    setIsAddRentalGuaranteeModalOpen(true);
  };

  const addRentalGuaranteeToList = async (rentalGuaranteeData: any) => {
    const savedRentalGuarantee =
      rentalGuaranteeData.customData.savedRentalGuarantee;

    const updateGuarantees = (prev: any, newGuarantee: any) => {
      const existingIndex = prev.guarantee.findIndex(
        (guarantee: any) => guarantee.id === newGuarantee.id,
      );
      if (existingIndex >= 0) {
        return {
          ...prev,
          guarantee: [
            ...prev.guarantee.slice(0, existingIndex),
            newGuarantee,
            ...prev.guarantee.slice(existingIndex + 1),
          ],
        };
      } else {
        return {
          ...prev,
          guarantee: [...(prev.guarantee || []), newGuarantee],
        };
      }
    };

    setContract((prev: any) => updateGuarantees(prev, savedRentalGuarantee));

    if (editedContract) {
      setEditedContract((prev: any) =>
        updateGuarantees(prev, savedRentalGuarantee),
      );
    }

    fetchRentalContracts();
  };

  const prepareChartData = (products: any, contract: any) => {
    const addMonths = (date: any, months: any) => {
      const d = new Date(date);
      d.setMonth(d.getMonth() + months);
      return d;
    };

    let minDate = new Date(
      Math.min(
        ...products.flatMap((product: any) =>
          product.price.map((price: any) =>
            new Date(price.effectiveDate).getTime(),
          ),
        ),
      ),
    );
    let maxDate = new Date();

    console.log("Earliest date from products:", minDate);
    console.log("Initial maxDate:", maxDate);

    minDate = addMonths(minDate, -2); // 2 months before the earliest date

    const nextIndexDate =
      contract && contract.nextIndexDate
        ? new Date(contract.nextIndexDate)
        : null;

    console.log(nextIndexDate);

    if (nextIndexDate && nextIndexDate > new Date()) {
      console.log("a");
      maxDate = addMonths(nextIndexDate, 2); // 2 months after the next indexation date
    } else {
      console.log("b");
      maxDate = addMonths(new Date(), 2); // 2 months after the current date
    }

    console.log("Extended maxDate:", maxDate);

    let allDates: any = [minDate.getTime(), maxDate.getTime()];

    if (nextIndexDate) {
      allDates.push(nextIndexDate.getTime());
    }

    products.forEach((product: any) => {
      product.price.forEach((price: any) => {
        allDates.push(new Date(price.effectiveDate).getTime());
      });
    });

    allDates = Array.from(new Set(allDates))
      .sort((a: any, b: any) => a - b)
      .map((time: any) => new Date(time));

    let maxY = Math.max(
      ...products.flatMap((product: any) =>
        product.price.map((price: any) => price.amount),
      ),
    );
    maxY *= 1.1; // 10% padding
    const today = new Date();
    const todayTime = today.getTime(); // Convert today to a timestamp

    const options = {
      scales: {
        x: {
          type: "time",
          time: {
            unit: "month",
          },
          grid: {
            drawOnChartArea: false,
          },
        },
        y: {
          suggestedMax: maxY,
        },
      },
      plugins: {
        annotation: {
          annotations: {
            lineCurrentDate: {
              type: "line",
              xMin: today,
              xMax: today,
              borderColor: "rgb(255, 99, 132)",
              borderWidth: 2,
              borderDash: [5, 5], // Makes the line dashed
              label: {
                content: "Today",
                enabled: true,
                position: "start",
              },
            },
          },
        },
      },
      interaction: {
        mode: "index",
        intersect: false,
      },
      elements: {
        line: {
          tension: 0,
        },
      },
    };

    const datasets = products.map((product: any) => {
      let lastPrice: any = null;
      const dataPoints = allDates.map((date: any) => {
        const price =
          product.price.find(
            (p: any) => new Date(p.effectiveDate).getTime() <= date.getTime(),
          ) || lastPrice;
        lastPrice = price || lastPrice;

        // If price is still undefined here, we should handle it
        if (!price) {
          return { x: date, y: null }; // Return null for y to indicate no data
        }

        // Check if the date is after today to apply dotted lines
        const isFutureDate = date.getTime() > todayTime; // Compare timestamps
        return {
          x: date,
          y: price.amount,
          meta: price.changeMetaData,
          borderDash: isFutureDate ? [5, 5] : undefined, // Apply dotted line for future dates
        };
      });

      const borderColor = `#${Math.floor(Math.random() * 16777215).toString(16)}`;

      return {
        label: product.name,
        data: dataPoints,
        fill: false,
        borderColor: borderColor,
      };
    });

    return {
      labels: allDates.map((date: any) => date.toLocaleDateString()),
      datasets: datasets,
      options: options,
      plugins: [annotationPlugin],
    };
  };

  const renderContractFileLogs = () => {
    return (
      <div className="col-span-full flex flex-col gap-2.5">
        <div className="flex justify-between">
          <h3 className="text-sm font-medium">Contractbestanden</h3>
          <div className="flex gap-6">
            <ButtonNew
              variant="secondaryNeutral"
              icon={<Upload />}
              label="Upload bestand"
              onClick={openUploadModal}
            />
          </div>
        </div>
        <Table>
          <TableHeader>
            <TableColumn>ID</TableColumn>
            <TableColumn>Bestandsnaam</TableColumn>
            <TableColumn>Documentdatum</TableColumn>
            <TableColumn>Tekenstatus</TableColumn>
            <TableColumn>Acties</TableColumn>
          </TableHeader>
          <TableBody>
            {contract.fileLogs &&
              contract.fileLogs.map((fileLog: FileLog, index: any) => {
                if (!fileLog) {
                  return (
                    <TableRow>
                      <TableCell> </TableCell>
                      <TableCell>Bestand niet gevonden</TableCell>
                      <TableCell> </TableCell>
                      <TableCell> </TableCell>
                      <TableCell> </TableCell>
                    </TableRow>
                  );
                } else {
                  return (
                    <TableRow key={index}>
                      <TableCell>{fileLog.id}</TableCell>
                      <TableCell>{fileLog.label}</TableCell>
                      <TableCell>
                        {(fileLog.documentDate &&
                          new Date(fileLog.documentDate) &&
                          formatDate(new Date(fileLog.documentDate))) ||
                          ""}
                      </TableCell>
                      <TableCell>{fileLog.signStatus}</TableCell>
                      <TableCell>
                        <a
                          href={fileLog.sharePointFileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <External />
                        </a>
                      </TableCell>
                    </TableRow>
                  );
                }
              })}
          </TableBody>
        </Table>
      </div>
    );
  };

  const renderDocumentStatuses = () => {
    if (!contract.documentStatuses?.length) return null;

    return (
      <div className="col-span-full flex flex-col gap-2.5">
        <h3 className="text-sm font-medium">Niet recupereerbare documenten</h3>
        <Table>
          <TableHeader>
            <TableColumn>Document</TableColumn>
            <TableColumn>Status</TableColumn>
            <TableColumn>Reden</TableColumn>
            <TableColumn>Datum</TableColumn>
          </TableHeader>
          <TableBody>
            {contract.documentStatuses.map((status: any, index: number) => (
              <TableRow key={index}>
                <TableCell>{status.documentType}</TableCell>
                <TableCell>
                  <Chip
                    size="sm"
                    classNames={{
                      base:
                        status.status === "MISSING_REQUIRED"
                          ? "bg-brick-primary/10 text-brick-primary"
                          : "bg-moss-primary/10 text-moss-primary",
                      content: "px-2 py-1",
                    }}
                  >
                    {status.status === "MISSING_REQUIRED"
                      ? "Vereist"
                      : "Niet vereist"}
                  </Chip>
                </TableCell>
                <TableCell>{status.reason}</TableCell>
                <TableCell>{formatDate(new Date(status.createdAt))}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  const toggleEditMode = () => {
    setEditMode((prevMode) => !prevMode);
    if (!editMode) {
      setEditedContract(contract); // initialize editedContract when entering edit mode
    }
  };

  // Get the most recent price for a product
  const getMostRecentPrice = (prices: any) => {
    const today = new Date();
    return prices
      .filter((price: any) => new Date(price.effectiveDate) <= today)
      .sort(
        (a: any, b: any) =>
          new Date(b.effectiveDate).getTime() -
          new Date(a.effectiveDate).getTime(),
      )[0];
  };

  // Function to get all future prices and their effective dates
  const getAllFuturePrices = (prices: any) => {
    const today = new Date();
    return prices
      .filter((price: any) => new Date(price.effectiveDate) > today)
      .sort(
        (a: any, b: any) =>
          new Date(a.effectiveDate).getTime() -
          new Date(b.effectiveDate).getTime(),
      );
  };

  const recalculateMilestones = async () => {
    try {
      const res = await fetchWithAuth(
        `${API_URL}/rental/contracts/${contract.id}/recalculate-milestones`,
        {
          method: "GET",
        },
        session,
      );

      console.log(res);

      const data: RentalContract = await res.json();

      if (res.ok) {
        console.log("Milestones recalculated:", data);
        setContract((prev: RentalContract) => ({
          ...prev,
          contractMilestoneDates: data.contractMilestoneDates,
        }));
        fetchRentalContracts();
        toast.success("Belangrijke gebeurtenissen zijn succesvol herberekend");
      } else {
        console.error("Error recalculating milestones:", data);
        toast.error(
          "Er is iets misgegaan bij het herberekenen van de belangrijke gebeurtenissen",
        );
      }
    } catch (e) {
      console.error("Error recalculating milestones:", e);
      toast.error(
        "Er is iets misgegaan bij het herberekenen van de belangrijke gebeurtenissen",
      );
    } finally {
      setIsRecalculateMilestonesModalOpen(false);
    }
  };

  const handleSave = () => {};

  const sharedProps = {
    editMode: editMode,
    isLoading: isLoading,
    onEditClick: toggleEditMode,
    onSaveClick: handleSave,
    editedState: editedContract,
    originalState: contract,
    setStateFunction: setEditedContract,
  };

  const propertyTaxesOptions = Object.values(PropertyTaxes).map((value) => ({
    value: value,
    label: value,
  }));

  const contractPhaseOptions = Object.values(ContractPhase).map((value) => ({
    value: value,
    label: value,
  }));

  const legalFormOptions = Object.values(ContractLegalForm).map((value) => ({
    value: value,
    label: value,
  }));

  const addFileLogToContract = (fileLog: any) => {
    contract.fileLogs.push(fileLog.fileLog);
    fetchRentalContracts();
  };

  const linkContactToContract = (contact: any) => {
    if (contract.contractContacts) {
      contract.contractContacts.push(contact);
    } else {
      contract.contractContacts = [contact];
    }

    fetchRentalContracts();
  };

  const handleUnlinkContractContact = async () => {
    if (!unlinkContactId) return;

    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/contract-contact/${unlinkContactId}`,
        { method: "DELETE" },
        session,
      );

      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        throw new Error(errorBody?.message || "Fout bij ontkoppelen");
      }

      toast.success("Contact ontkoppeld van contract");
      setContract((prev) => ({
        ...prev,
        contractContacts: prev.contractContacts?.filter(
          (cc: ContractContact) => cc.id !== unlinkContactId,
        ),
      }));
      fetchRentalContracts();
    } catch (error: any) {
      toast.error(error.message || "Fout bij ontkoppelen van contact");
    } finally {
      setUnlinkContactId(null);
      setUnlinkContactName("");
    }
  };

  const getUserName = (userId: string) => {
    const foundUser = users.find((user) => user.id === userId);

    return foundUser?.first_name + " " + foundUser?.last_name;
  };

  const addMilestoneDateToContract = (milestoneDate: any) => {
    if (contract.contractMilestoneDates) {
      contract.contractMilestoneDates.push(milestoneDate);
    } else {
      contract.contractMilestoneDates = [milestoneDate];
    }

    fetchRentalContracts();
  };

  // Define the sections and fields to be rendered
  const sections = [
    {
      title: "Algemeen",
      icon: <Edit />,
      fieldsConfig: [
        { label: "Contract ID", key: "id", Component: EditableInputComponent },
        {
          label: "Aantal huidige bewoners",
          key: "numResidents",
          Component: EditableInputComponent,
        },
        {
          label: "Contractfase",
          key: "phase",
          Component: () => (
            <EditableSelectComponent
              label="Contractfase"
              editMode={editMode}
              value={editedContract?.phase || contract?.phase || ""}
              options={contractPhaseOptions}
              onChange={(value: string) =>
                setEditedContract((prev: any) => ({ ...prev, phase: value }))
              }
            />
          ),
        },
        {
          label: "Juridische vorm",
          key: "legalForm",
          Component: () => (
            <EditableSelectComponent
              label="Juridische vorm"
              editMode={editMode}
              value={editedContract?.legalForm || contract?.legalForm || ""}
              options={legalFormOptions}
              onChange={(value: string) =>
                setEditedContract((prev: any) => ({
                  ...prev,
                  legalForm: value,
                }))
              }
            />
          ),
        },
        // Add more fields as needed
      ],
      ...sharedProps,
    },
    {
      title: "Facturatie",
      icon: <Edit />,
      fieldsConfig: [
        {
          label: "Facturatiefrequentie",
          key: "invoicingFrequency",
          Component: EditableInputComponent,
        },
        {
          label: "Volgende Factuurdatum",
          key: "nextInvoiceDate",
          Component: EditableInputComponent,
        },
        {
          label: "Volgende Indexdatum",
          key: "nextIndexDate",
          Component: EditableInputComponent,
        },
        {
          label: "Onroerende voorheffing",
          key: "propertyTaxes",
          Component: () => (
            <EditableSelectComponent
              label="Onroerende voorheffing"
              editMode={editMode}
              value={
                editedContract?.propertyTaxes || contract?.propertyTaxes || ""
              }
              options={propertyTaxesOptions}
              onChange={(value: string) =>
                setEditedContract((prev: any) => ({
                  ...prev,
                  propertyTaxes: value,
                }))
              }
            />
          ),
        },
        // Add more fields as needed
      ],
      ...sharedProps,
    },
    // Other sections can be added here
  ];

  const deleteMilestoneDate = async (milestoneDateId: string) => {
    try {
      const res = await fetchWithAuth(
        `${API_URL}/rental/milestone-dates/${milestoneDateId}`,
        {
          method: "DELETE",
        },
        session,
      );

      console.log(res);

      if (res.ok) {
        setContract((prev: RentalContract) => ({
          ...prev,
          contractMilestoneDates: prev.contractMilestoneDates?.filter(
            (milestoneDate: any) => milestoneDate.id !== milestoneDateId,
          ),
        }));
        fetchRentalContracts();
        toast.success("Belangrijke gebeurtenis succesvol verwijderd");
      } else {
        console.error("Error deleting milestone date:", res);
        toast.error(
          "Er is iets misgegaan bij het verwijderen van de belangrijke gebeurtenis",
        );
      }
    } catch (e) {
      console.error("Error deleting milestone date:", e);
      toast.error(
        "Er is iets misgegaan bij het verwijderen van de belangrijke gebeurtenis",
      );
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="4xl"
        shouldBlockScroll={true}
        scrollBehavior="inside"
        backdrop="blur"
        className="p-8"
      >
        <ModalContent className="text-neutral-primary flex flex-col gap-4 font-normal">
          <ModalHeader className="flex flex-row items-center justify-between p-0 font-normal">
            <p>
              {contract &&
                contract.contractContacts
                  .filter(
                    (contact: ContractContact) =>
                      contact.contactType == ContactType.TENANT,
                  )
                  .map((contact: any) => contact.resource?.name)
                  .join(", ")}
            </p>
            <div className="flex gap-6">
              <ButtonNew
                variant="secondaryNeutral"
                icon={<Edit />}
                label="Wijzig gegevens"
                onClick={toggleEditMode}
              />
            </div>
          </ModalHeader>
          <ModalBody className="grid gap-5 gap-y-8 px-0 sm:grid-cols-2">
            {contract && sections.map((section) => renderSection(section))}
            {contract && renderContractFileLogs()}
            {contract && renderDocumentStatuses()}
            {contract && (
              <>
                {/* Contacts Table */}
                <div className="col-span-full flex flex-col gap-2.5">
                  <div className="flex justify-between">
                    <h3 className="text-sm font-medium">Contactpersonen</h3>
                    <span className="text-sm font-semibold text-red-500">
                      Rijksregisternummer handmatig invoeren bij contact!
                      (calendar app doet die niet automatisch)
                    </span>
                    <div className="flex gap-6">
                      <ButtonNew
                        variant="secondaryNeutral"
                        icon={<PlugsConnected />}
                        label="Koppel contact"
                        onClick={onOpenConnectContactModal}
                      />
                    </div>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableColumn>Contact Type</TableColumn>
                      <TableColumn>Contact Naam</TableColumn>
                      <TableColumn>Acties</TableColumn>
                    </TableHeader>
                    <TableBody>
                      {(contract.contractContacts as ContractContact[]).map(
                        (contact: ContractContact, index: any) => (
                          <TableRow key={index}>
                            <TableCell>{contact.contactType}</TableCell>
                            <TableCell>{contact.resource?.name || <span className="text-neutral-secondary-mid italic">Verwijderd contact</span>}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {contact.resource && (
                                  <Link
                                    to={`/${contact.resource.type}/${contact.resource.resourceId}`}
                                  >
                                    <Eye />
                                  </Link>
                                )}
                                <Tooltip content="Ontkoppel contact">
                                  <button
                                    onClick={() => {
                                      setUnlinkContactId(contact?.id as any);
                                      setUnlinkContactName(contact.resource?.name || "Onbekend contact");
                                    }}
                                    className="text-neutral-secondary-mid hover:text-red-500 transition-colors"
                                  >
                                    <LinkBreak size={18} />
                                  </button>
                                </Tooltip>
                              </div>
                            </TableCell>
                          </TableRow>
                        ),
                      )}
                    </TableBody>
                  </Table>
                </div>

                {/* Guarantees Table */}
                <div className="col-span-full flex flex-col gap-2.5">
                  <div className="flex justify-between">
                    <h3 className="text-sm font-medium">
                      Huurgaranties / waarborgen
                    </h3>
                    <div className="flex gap-6">
                      <ButtonNew
                        variant="secondaryNeutral"
                        icon={<Document />}
                        label="Voeg huurgarantie of waarborg toe"
                        onClick={onOpenAddRentalGuarantee}
                      />
                    </div>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableColumn>Type</TableColumn>
                      <TableColumn>Bedrag</TableColumn>
                      <TableColumn>Referentie</TableColumn>
                      <TableColumn>Document</TableColumn>
                      <TableColumn>Tegenpartij</TableColumn>
                    </TableHeader>
                    <TableBody>
                      {(contract.guarantee as any).map(
                        (guarantee: RentalGuarantee, index: any) => (
                          <TableRow key={index}>
                            <TableCell>{guarantee.type}</TableCell>
                            <TableCell>
                              €{guarantee.amount.toFixed(2)}
                            </TableCell>
                            <TableCell>{guarantee.reference}</TableCell>
                            <TableCell>
                              {guarantee.fileLog ? (
                                <a
                                  href={
                                    guarantee.fileLog &&
                                    guarantee.fileLog.sharePointFileUrl
                                  }
                                  target="_blank"
                                >
                                  <File />
                                </a>
                              ) : (
                                <UploadSimple
                                  className="cursor-pointer"
                                  onClick={() =>
                                    setIsAddRentalGuaranteeModalOpen(true)
                                  }
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              {guarantee.fileLog
                                ? guarantee.fileLog?.relations
                                    ?.map((relation: Resource) => relation.name)
                                    .join(", ")
                                : "Nog geen tegenpartij"}
                            </TableCell>
                          </TableRow>
                        ),
                      )}
                    </TableBody>
                  </Table>
                </div>

                {/* Products Table */}
                <div className="col-span-full flex flex-col gap-2.5">
                  <h3 className="text-sm font-medium">Producten</h3>
                  <Table>
                    <TableHeader>
                      <TableColumn>Naam</TableColumn>
                      <TableColumn>Product ID</TableColumn>
                      <TableColumn>Indexeerbaar</TableColumn>

                      <TableColumn>Huidige Prijs</TableColumn>
                      <TableColumn>Toekomstige Prijzen</TableColumn>
                      <TableColumn>Acties</TableColumn>
                      {/* Add more columns as needed */}
                    </TableHeader>
                    <TableBody>
                      {(contract.products as any).map(
                        (product: any, index: any) => {
                          const recentPrice = getMostRecentPrice(product.price);
                          const futurePrices = getAllFuturePrices(
                            product.price,
                          );

                          return (
                            <TableRow key={index}>
                              <TableCell>{product.name}</TableCell>
                              <TableCell>{product.odooProductId}</TableCell>
                              <TableCell>
                                {product.indexable ? "Ja" : "Nee"}
                              </TableCell>
                              <TableCell>
                                {recentPrice
                                  ? `€${recentPrice.amount.toFixed(2)}`
                                  : "N/A"}
                              </TableCell>
                              <TableCell>
                                {futurePrices && futurePrices.length > 0 ? (
                                  <>
                                    <span>
                                      {futurePrices.length > 0 && (
                                        <div>
                                          {futurePrices.map(
                                            (fp: any, idx: any) => (
                                              <span key={idx}>
                                                <br />
                                                Toekomstige prijs: €
                                                {fp.amount.toFixed(
                                                  2,
                                                )} (vanaf{" "}
                                                {new Date(
                                                  fp.effectiveDate,
                                                ).toLocaleDateString()}
                                                )
                                              </span>
                                            ),
                                          )}
                                        </div>
                                      )}
                                    </span>
                                  </>
                                ) : (
                                  "NVT"
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Tooltip content="Stel nieuwe prijs in">
                                    <Calendar
                                      weight="duotone"
                                      size={20}
                                      className="text-moss-primary cursor-pointer"
                                      onClick={() => {
                                        setSelectedProductForFuturePrice(
                                          product,
                                        );
                                        setIsSetFuturePriceModalOpen(true);
                                      }}
                                    />
                                  </Tooltip>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        },
                      )}
                    </TableBody>
                  </Table>
                </div>
                {/* Milestones Table */}
                <div className="col-span-full flex flex-col gap-2.5">
                  <div className="flex justify-between">
                    <div className="flex gap-2">
                      <h3 className="text-sm font-medium">
                        Belangrijke gebeurtenissen
                      </h3>
                      <Tooltip
                        content={
                          <div className="px-1 py-2">
                            <a
                              className="text-brick-primary"
                              href="https://www.vlaanderen.be/einde-en-opzegging-van-het-huurcontract"
                              target="_blank"
                            >
                              Bron opzegtermijnen
                            </a>
                            <div className="m-2">
                              <h2>Opzegtermijn verhuurder</h2>
                              <p>
                                De verhuurder kan de huurovereenkomst opzeggen
                                om de woning te verbouwen of te renoveren. Bij
                                huurcontracten die voor 1 januari 2019 zijn
                                gesloten mag dit alleen op het einde van elke
                                driejarige huurperiode. Bij huurcontracten die
                                sinds 1 januari 2019 zijn gesloten, mag de
                                verhuurder dit op elk ogenblik zodra het derde
                                huurjaar is verstreken.
                              </p>
                              <ul>
                                <li>
                                  Het moet daarbij gaan om grondige
                                  verbouwingswerken die bewoning tijdelijk
                                  onmogelijk maken. Een gewone opknapbeurt is
                                  dus geen motief voor opzegging.
                                </li>
                                <li>
                                  U moet bij de opzeg een bewijs voegen van de
                                  geplande werken (bouwvergunning,
                                  gedetailleerde beschrijving van de werken of
                                  bestek, aannemingsovereenkomst).
                                </li>
                                <li>
                                  De werken moeten ten laatste 6 maanden na het
                                  einde van de opzegperiode aanvangen.
                                </li>
                              </ul>
                              <p>
                                Komt u die verplichting niet na, dan heeft de
                                huurder recht op een schadevergoeding van 18
                                maanden huur.
                              </p>
                            </div>
                            <div className="m-2">
                              <h2>Opzegtermijn huurder</h2>
                              <p>
                                De huurder mag de overeenkomst altijd opzeggen
                                zonder reden.
                              </p>
                              <ul>
                                <li>
                                  met een opzeggingstermijn van 3 maanden. De
                                  opzeg begint te lopen op de eerste dag van de
                                  maand, ná de maand waarin de opzegging is
                                  gedaan.
                                </li>
                                <li>
                                  en een opzeggingsvergoeding van 3 maanden huur
                                  als het huurcontract (na de opzegperiode)
                                  eindigt tijdens het eerste jaar, 2 maanden
                                  huur als het huurcontract (na de opzegperiode)
                                  eindigt tijdens het tweede jaar, 1 maand huur
                                  als het huurcontract (na de opzegperiode)
                                  eindigt tijdens het derde jaar.{" "}
                                </li>
                                <li>
                                  Na het derde jaar is er geen opzegvergoeding
                                  meer te betalen.
                                </li>
                              </ul>
                              <p>
                                LET OP: als de huurovereenkomst na twee maanden
                                nog niet is geregistreerd, geldt er geen
                                opzegtermijn of opzegvergoeding, en kan de
                                huurder dus - zolang de huur niet is
                                geregistreerd - op elk moment en zonder kosten
                                de huur onmiddellijk beëindigen.
                              </p>
                            </div>
                          </div>
                        }
                      >
                        <Info className="text-neutral-primary h-[17px] w-[17px]" />
                      </Tooltip>
                    </div>
                    <div className="flex gap-6">
                      <Tooltip content="Indien de wetgeving zou veranderen kan deze functie gebruikt worden om de nieuwe belangrijke gebeurtenissen te herberekenen op basis van de startdatum van het contract">
                        <ButtonNew
                          variant="secondaryNeutral"
                          icon={<Recycle />}
                          label="Herbereken systeem gebeurtenissen"
                          onClick={openRecalculateMilestonesModal}
                        />
                      </Tooltip>
                      <ButtonNew
                        variant="secondaryNeutral"
                        icon={<PlusCircle />}
                        label="Voeg belangrijke gebeurtenis toe"
                        onClick={onOpenAddMilestoneDateModal}
                      />
                    </div>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableColumn>Origine</TableColumn>
                      <TableColumn>Beschrijving</TableColumn>
                      <TableColumn>Datum</TableColumn>
                      <TableColumn>Verwijder</TableColumn>
                      {/* Add more columns as needed */}
                    </TableHeader>
                    <TableBody>
                      {(contract.contractMilestoneDates as any) &&
                        isArray(contract.contractMilestoneDates) &&
                        (contract.contractMilestoneDates as any)
                          .sort(
                            (a: any, b: any) =>
                              new Date(a.date).getTime() -
                              new Date(b.date).getTime(),
                          )
                          .map((milestone: any, index: any) => (
                            <TableRow key={index}>
                              <TableCell>
                                {milestone.origin == MilestoneOrigin.SYSTEM
                                  ? "Systeem"
                                  : MilestoneOrigin.USER && milestone.userId
                                    ? getUserName(milestone.userId)
                                    : "Onbekend"}
                              </TableCell>
                              <TableCell>
                                {milestone.milestoneDateType !=
                                MilestoneDateType.CUSTOM
                                  ? milestone.milestoneDateType
                                  : milestone.customDescription}
                              </TableCell>
                              <TableCell
                                className={
                                  isDatePast(milestone.date)
                                    ? "text-brick-primary"
                                    : "text-moss-primary font-bold"
                                }
                              >
                                {formatDate(new Date(milestone.date))}
                              </TableCell>
                              <TableCell>
                                <div className="flex gap-2">
                                  <Trash
                                    className="text-brick-primary cursor-pointer"
                                    onClick={() =>
                                      deleteMilestoneDate(milestone?.id)
                                    }
                                  />
                                </div>
                              </TableCell>
                              {/* Additional cells can be added here */}
                            </TableRow>
                          ))}
                    </TableBody>
                  </Table>
                </div>
                {contract && <LineChart data={chartData} />}
              </>
            )}
          </ModalBody>
          <ModalUploadFileLog
            isOpen={isUploadModalOpen}
            onClose={() => setIsUploadModalOpen(false)}
            building={uploadBuilding}
            unit={uploadUnit}
            onFileUploadSuccess={addFileLogToContract}
            incomingCustomData={{
              rentalContractId: contract && contract.id,
              ...attributeUploadCustomData,
            }}
          />
        </ModalContent>
      </Modal>
      <ModalUploadFileLog
        isOpen={isAddRentalGuaranteeModalOpen}
        onClose={() => setIsAddRentalGuaranteeModalOpen(false)}
        building={uploadBuilding}
        unit={uploadUnit}
        onFileUploadSuccess={addRentalGuaranteeToList}
        defaultFileLabel={FileLabel.Huurwaarborg}
        customLabels={[FileLabel.Huurwaarborg]}
        incomingCustomData={{
          rentalContractId: contract && contract.id,
          ...attributeUploadCustomData,
          rentalGuaranteeId:
            contract &&
            contract.guarantee &&
            contract.guarantee.length &&
            contract.guarantee[contract.guarantee.length - 1].id,
        }}
      />
      <ConnectContactModal
        contract={contract as RentalContract}
        isOpen={isConnectContactModalOpen}
        onClose={() => setIsConnectContactModalOpen(false)}
        linkContactToContract={linkContactToContract}
      />
      <AddMilestoneDateModal
        contract={contract as RentalContract}
        isOpen={isAddMilestoneDateModalOpen}
        onClose={() => setIsAddMilestoneDateModalOpen(false)}
        addMilestoneDateToContract={addMilestoneDateToContract}
      />
      <ConfirmationModal
        title="Herbereken belangrijke gebeurtenissen"
        description="Weet u zeker dat u de belangrijke gebeurtenissen opnieuw wilt berekenen? De bedoeling van deze functie is om data opnieuw te berekenen vanaf de startdatum bij wetgevende wijzigingen"
        isOpen={isRecalculateMilestonesModalOpen}
        onClose={() => setIsRecalculateMilestonesModalOpen(false)}
        confirmationAction={recalculateMilestones}
        confirmationIcon={<Recycle />}
        confirmationText="Herbereken"
        declineText="Annuleren"
      />
      <ConfirmationModal
        title="Contact ontkoppelen"
        description={`Weet u zeker dat u "${unlinkContactName}" wilt ontkoppelen van dit contract? Het contact zelf wordt niet verwijderd.`}
        isOpen={unlinkContactId !== null}
        onClose={() => {
          setUnlinkContactId(null);
          setUnlinkContactName("");
        }}
        confirmationAction={handleUnlinkContractContact}
        confirmationIcon={<LinkBreak />}
        confirmationText="Ontkoppelen"
        declineText="Annuleren"
      />
      <SetFuturePriceModal
        isOpen={isSetFuturePriceModalOpen}
        onClose={() => {
          setIsSetFuturePriceModalOpen(false);
          setSelectedProductForFuturePrice(null);
        }}
        product={selectedProductForFuturePrice}
        onSuccess={fetchRentalContracts}
      />
    </>
  );
};

export default ModalViewContract;
